import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import { format, parseISO, addDays } from 'date-fns';
import { nl } from 'date-fns/locale';
import { t } from '../../i18n/nl.js';

function fmtDate(str) {
  if (!str) return '—';
  try { return format(parseISO(str), 'd MMMM yyyy', { locale: nl }); }
  catch { return str; }
}

function fmtDateObj(d) {
  try { return format(d, 'd MMMM yyyy', { locale: nl }); }
  catch { return '—'; }
}

function euro(val) {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(val ?? 0);
}

const GROEN       = '#1d9e75';
const DONKERGROEN = '#1a5c3a';
const LICHTGROEN  = '#f0faf5';
const GRIJS       = '#6b7280';
const ROOD        = '#dc2626';
const RAND        = '#e5e7eb';
const LICHTGRIJS  = '#f9f9f9';

const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#1a1a2e',
    padding: '36 44 56 44',
    lineHeight: 1.4,
  },

  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 16,
    borderBottom: `2 solid ${GROEN}`,
    marginBottom: 0,
  },
  headerRowFinal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 16,
    borderBottom: `2 solid ${ROOD}`,
    marginBottom: 0,
  },
  headerLeft:  { flex: 2 },
  headerRight: { flex: 1.8, alignItems: 'flex-end' },

  herinneringTitle: {
    fontSize: 26,
    fontFamily: 'Helvetica-Bold',
    color: GROEN,
    marginBottom: 16,
    textAlign: 'center',
  },
  herinneringTitleFinal: {
    fontSize: 26,
    fontFamily: 'Helvetica-Bold',
    color: ROOD,
    marginBottom: 16,
    textAlign: 'center',
  },
  herinneringSubtitel: {
    fontSize: 10,
    color: GRIJS,
    marginBottom: 12,
    textAlign: 'center',
  },
  herinneringSubtitelFinal: {
    fontSize: 10,
    color: ROOD,
    marginBottom: 12,
    textAlign: 'center',
  },
  clientName: { fontSize: 11, fontFamily: 'Helvetica-Bold', marginBottom: 2, marginTop: 20 },
  clientLine: { fontSize: 9, color: GRIJS, marginBottom: 1 },

  logoImg:     { width: 60, height: 60, objectFit: 'contain', marginBottom: 6 },
  infoLine:    { fontSize: 9, textAlign: 'right', marginBottom: 3, color: '#555' },
  companyName: { fontSize: 10, fontFamily: 'Helvetica-Bold', textAlign: 'right', marginBottom: 2 },
  companyLine: { fontSize: 8.5, color: GRIJS, textAlign: 'right', marginBottom: 1 },

  infoBlok: {
    marginTop: 20,
    marginBottom: 16,
    borderRadius: 6,
    padding: '10 12',
    backgroundColor: LICHTGRIJS,
    border: `1 solid ${RAND}`,
  },
  infoRow:     { flexDirection: 'row', marginBottom: 5 },
  infoKey:     { width: 100, fontSize: 9, color: GRIJS },
  infoVal:     { flex: 1, fontSize: 9 },
  infoValRood: { flex: 1, fontSize: 11, fontFamily: 'Helvetica-Bold', color: ROOD },

  tekst: {
    fontSize: 9,
    lineHeight: 1.7,
    marginBottom: 12,
    color: '#333',
  },
  tekstRood: {
    fontSize: 9,
    lineHeight: 1.7,
    marginBottom: 12,
    color: ROOD,
    fontFamily: 'Helvetica-Bold',
  },
  aanmaningBlok: {
    backgroundColor: '#fef2f2',
    borderRadius: 6,
    padding: '10 12',
    marginBottom: 16,
    border: `1 solid #fecaca`,
  },

  betaalBox: {
    backgroundColor: LICHTGROEN,
    borderRadius: 6,
    padding: '10 12',
    marginBottom: 20,
  },
  betaalBoxFinal: {
    backgroundColor: '#fef2f2',
    borderRadius: 6,
    padding: '10 12',
    marginBottom: 20,
    border: `1 solid #fecaca`,
  },
  sectionTitle: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: GROEN,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  },
  sectionTitleFinal: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: ROOD,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  },
  betaalRow: { flexDirection: 'row', marginBottom: 3 },
  betaalKey: { width: 80, fontSize: 8, color: GRIJS },
  betaalVal: { flex: 1, fontSize: 8 },

  tableHeader: {
    flexDirection: 'row',
    backgroundColor: DONKERGROEN,
    padding: '6 8',
    borderRadius: 4,
    marginTop: 4,
  },
  tableHeaderFinal: {
    flexDirection: 'row',
    backgroundColor: '#991b1b',
    padding: '6 8',
    borderRadius: 4,
    marginTop: 4,
  },
  thText: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: '#fff',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  tableRow:     { flexDirection: 'row', padding: '6 8', borderBottom: `1 solid ${RAND}` },
  tableRowEven: { backgroundColor: LICHTGRIJS },
  tdText:  { fontSize: 9 },
  tdMuted: { fontSize: 9, color: GRIJS },

  colDesc:   { flex: 3 },
  colQty:    { width: 36, textAlign: 'right' },
  colPrice:  { width: 60, textAlign: 'right' },
  colBtwPct: { width: 30, textAlign: 'right' },
  colIncl:   { width: 70, textAlign: 'right' },

  bottomRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 16 },
  btwTable:  { flex: 1.2, marginRight: 20, alignSelf: 'flex-start' },
  btwHeader: { flexDirection: 'row', borderBottom: `1 solid ${RAND}`, paddingBottom: 4, marginBottom: 5 },
  btwTh:     { flex: 1, fontSize: 7.5, color: GRIJS, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily: 'Helvetica-Bold' },
  btwRow:    { flexDirection: 'row' },
  btwCell:   { flex: 1, fontSize: 9 },
  specBlok:  { flex: 1 },
  specRij:   { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
  specLabel: { flex: 1, fontSize: 9, color: GRIJS },
  specValue: { fontSize: 9, textAlign: 'right' },
  specBoldLabel: { fontSize: 11, fontFamily: 'Helvetica-Bold' },
  specBoldValue: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: GROEN },
  specBoldValueRood: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: ROOD },

  footnote: {
    marginTop: 20,
    paddingTop: 8,
    borderTop: `1 solid ${RAND}`,
    fontSize: 8,
    color: GRIJS,
  },

  footer: {
    position: 'absolute',
    bottom: 24,
    left: 44,
    right: 44,
    borderTop: `1 solid ${RAND}`,
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: { fontSize: 7.5, color: GRIJS },
});

export default function HerinneringDocument({ invoice, lines = [], company = {}, type = 'reminder' }) {
  const isFinal = type === 'final_notice';
  const { invoice_number, total_amount, created_at, client } = invoice;

  const companyName     = company.company_name              || 'Fit En Sterker';
  const companyAddress  = company.company_address           || '';
  const companyPostcode = company.company_postcode          || '';
  const companyCity     = company.company_city              || '';
  const companyEmail    = company.company_email             || '';
  const companyKvk      = company.company_kvk               || '';
  const companyBtw      = company.company_btw               || '';
  const companyIban     = company.company_iban              || '';
  const companyLogo     = company.company_logo_url          || '';
  const termDays        = parseInt(company.company_payment_term_days || '14');
  const defaultFooter   = company.invoice_footer            || '';
  const invoiceFooter   = isFinal
    ? (company.invoice_footer_final_notice || defaultFooter)
    : (company.invoice_footer_reminded     || defaultFooter);
  const bodyText = isFinal
    ? (company.final_notice_pdf_text || '')
    : (company.reminder_pdf_text     || '');

  const cityLine = [companyPostcode, companyCity].filter(Boolean).join(' ');

  const today = new Date();
  const offsetDays = isFinal ? 14 : 7;
  const newDeadline = addDays(today, offsetDays);
  const newDeadlineStr = fmtDateObj(newDeadline);

  let dueDate = null;
  try {
    dueDate = addDays(parseISO(created_at), termDays);
  } catch { /* valt terug op '—' */ }

  const dueDateStr = dueDate ? fmtDateObj(dueDate) : '—';

  const headerRowStyle    = isFinal ? s.headerRowFinal        : s.headerRow;
  const titleStyle        = isFinal ? s.herinneringTitleFinal : s.herinneringTitle;
  const tableHeaderStyle  = isFinal ? s.tableHeaderFinal      : s.tableHeader;
  const betaalBoxStyle    = isFinal ? s.betaalBoxFinal        : s.betaalBox;
  const sectionTitleStyle = isFinal ? s.sectionTitleFinal     : s.sectionTitle;

  const titleText = isFinal
    ? t('pdf.laaststeAanmaning')
    : t('pdf.betalingsherinnering');

  const btwSpec = {};
  lines.forEach(line => {
    const pct  = line.btw_percentage ?? 9;
    const excl = (line.amount ?? (line.quantity * line.unit_price)) || 0;
    const btw  = Math.round(excl * (pct / 100) * 100) / 100;
    if (!btwSpec[pct]) btwSpec[pct] = { grondslag: 0, btw: 0 };
    btwSpec[pct].grondslag += excl;
    btwSpec[pct].btw       += btw;
  });
  const btwTarieven = Object.keys(btwSpec).map(Number).sort((a, b) => a - b);
  const totaalExcl  = Object.values(btwSpec).reduce((sum, v) => sum + v.grondslag, 0);

  return (
    <Document>
      <Page size="A4" style={s.page}>

        {/* ── HEADER ─────────────────────────────────────────────── */}
        <View style={headerRowStyle}>
          <View style={s.headerLeft}>
            <Text style={titleStyle}>{titleText}</Text>
            <Text style={s.clientName}>{client?.full_name || '—'}</Text>
            {client?.email && <Text style={s.clientLine}>{client.email}</Text>}
            {(client?.street || client?.house_number) && (
              <Text style={s.clientLine}>
                {[client.street, client.house_number].filter(Boolean).join(' ')}
              </Text>
            )}
            {(client?.postal_code || client?.city) && (
              <Text style={s.clientLine}>
                {[client.postal_code, client.city].filter(Boolean).join(' ')}
              </Text>
            )}
          </View>

          <View style={s.headerRight}>
            {companyLogo ? <Image src={companyLogo} style={s.logoImg} /> : null}
            {companyBtw && <Text style={s.infoLine}>{t('pdf.btwnr')}  {companyBtw}</Text>}
            {companyKvk && <Text style={s.infoLine}>{t('pdf.kvk')}  {companyKvk}</Text>}
            <Text style={s.companyName}>{companyName}</Text>
            {companyAddress ? <Text style={s.companyLine}>{companyAddress}</Text> : null}
            {cityLine       ? <Text style={s.companyLine}>{cityLine}</Text>       : null}
            {companyEmail   ? <Text style={s.companyLine}>{companyEmail}</Text>   : null}
          </View>
        </View>

        {/* ── INFO BLOK ──────────────────────────────────────────── */}
        <View style={s.infoBlok}>
          <View style={s.infoRow}>
            <Text style={s.infoKey}>{t('pdf.factuurnummer')}</Text>
            <Text style={s.infoVal}>{invoice_number}</Text>
          </View>
          <View style={s.infoRow}>
            <Text style={s.infoKey}>{t('pdf.factuurdatumLabel')}</Text>
            <Text style={s.infoVal}>{fmtDate(created_at)}</Text>
          </View>
          <View style={s.infoRow}>
            <Text style={s.infoKey}>{t('pdf.vervaldatum')}</Text>
            <Text style={s.infoVal}>{dueDateStr}</Text>
          </View>
          <View style={[s.infoRow, { marginBottom: 0 }]}>
            <Text style={s.infoKey}>{t('pdf.openstaand')}</Text>
            <Text style={s.infoValRood}>{euro(total_amount)}</Text>
          </View>
        </View>

        {/* ── TEKST ──────────────────────────────────────────────── */}
        {isFinal ? (
          <View style={s.aanmaningBlok}>
            <Text style={s.tekst}>
              {bodyText || (
                t('pdf.aanmaningTekst1') + ' ' + euro(total_amount) + ' ' +
                t('pdf.aanmaningTekst2') + ' ' + newDeadlineStr + ' ' +
                t('pdf.aanmaningTekst3') + '\n\n' + t('pdf.aanmaningTekst4')
              )}
            </Text>
          </View>
        ) : (
          <Text style={s.tekst}>
            {bodyText || (
              t('pdf.herinneringTekst1') + ' ' + euro(total_amount) + ' ' +
              t('pdf.herinneringTekst2') + ' ' + newDeadlineStr + ' ' +
              t('pdf.herinneringTekst3')
            )}
          </Text>
        )}

        {/* ── BETAALGEGEVENS ─────────────────────────────────────── */}
        <View style={betaalBoxStyle}>
          <Text style={sectionTitleStyle}>{t('pdf.betaalgegevens')}</Text>
          {companyIban ? (
            <View style={s.betaalRow}>
              <Text style={s.betaalKey}>{t('pdf.naarIban')}</Text>
              <Text style={s.betaalVal}>{companyIban}</Text>
            </View>
          ) : null}
          <View style={s.betaalRow}>
            <Text style={s.betaalKey}>{t('pdf.opNaamVan')}</Text>
            <Text style={s.betaalVal}>{companyName}</Text>
          </View>
          <View style={s.betaalRow}>
            <Text style={s.betaalKey}>{t('pdf.omschrijvingBet')}</Text>
            <Text style={s.betaalVal}>{t('pdf.factuur')} {invoice_number}</Text>
          </View>
        </View>

        {/* ── ORIGINELE FACTUURREGELS ─────────────────────────────── */}
        <Text style={sectionTitleStyle}>{t('pdf.oorspronkelijkeRegels')}</Text>
        <View style={tableHeaderStyle}>
          <Text style={[s.thText, s.colDesc]}>{t('pdf.omschrijving')}</Text>
          <Text style={[s.thText, s.colQty]}>{t('pdf.aantal')}</Text>
          <Text style={[s.thText, s.colPrice]}>{t('pdf.stukprijs')}</Text>
          <Text style={[s.thText, s.colBtwPct]}>{t('pdf.colBtwPct')}</Text>
          <Text style={[s.thText, s.colIncl]}>{t('pdf.colIncl')}</Text>
        </View>

        {lines.map((line, idx) => {
          const excl   = line.amount ?? (line.quantity * line.unit_price);
          const pct    = line.btw_percentage ?? 9;
          const btwAmt = Math.round(excl * (pct / 100) * 100) / 100;
          const incl   = excl + btwAmt;
          return (
            <View key={line.id ?? idx} style={[s.tableRow, idx % 2 === 1 ? s.tableRowEven : {}]}>
              <Text style={[s.tdText, s.colDesc]}>{line.description}</Text>
              <Text style={[s.tdText, s.colQty]}>{line.quantity}</Text>
              <Text style={[s.tdText, s.colPrice]}>{euro(line.unit_price)}</Text>
              <Text style={[s.tdMuted, s.colBtwPct]}>{pct}%</Text>
              <Text style={[s.tdText, s.colIncl]}>{euro(incl)}</Text>
            </View>
          );
        })}

        {lines.length === 0 && (
          <View style={s.tableRow}>
            <Text style={[s.tdMuted, { flex: 1, textAlign: 'center' }]}>{t('pdf.geenRegeldetails')}</Text>
          </View>
        )}

        {/* ── BTW-SPECIFICATIE + TOTALEN ──────────────────────────── */}
        <View style={s.bottomRow}>
          <View style={s.btwTable}>
            <Text style={[sectionTitleStyle, { marginBottom: 6 }]}>{t('pdf.btwSpecificatie')}</Text>
            <View style={s.btwHeader}>
              <Text style={s.btwTh}>{t('pdf.btwPercent')}</Text>
              <Text style={s.btwTh}>{t('pdf.grondslag')}</Text>
              <Text style={s.btwTh}>{t('pdf.bedrag')}</Text>
            </View>
            {btwTarieven.map(pct => (
              <View key={pct} style={s.btwRow}>
                <Text style={s.btwCell}>{pct}%</Text>
                <Text style={s.btwCell}>{euro(btwSpec[pct].grondslag)}</Text>
                <Text style={s.btwCell}>{euro(btwSpec[pct].btw)}</Text>
              </View>
            ))}
            {btwTarieven.length === 0 && (
              <View style={s.btwRow}>
                <Text style={[s.btwCell, { color: GRIJS }]}>—</Text>
              </View>
            )}
          </View>

          <View style={s.specBlok}>
            <View style={s.specRij}>
              <Text style={s.specLabel}>{t('pdf.totaalExclBtw')}</Text>
              <Text style={s.specValue}>{euro(totaalExcl)}</Text>
            </View>
            {btwTarieven.map(pct => (
              <View key={pct} style={s.specRij}>
                <Text style={s.specLabel}>{t('pdf.btw')} {pct}%</Text>
                <Text style={s.specValue}>{euro(btwSpec[pct].btw)}</Text>
              </View>
            ))}
            {btwTarieven.length === 0 && (
              <View style={s.specRij}>
                <Text style={s.specLabel}>{t('pdf.btw')}</Text>
                <Text style={s.specValue}>{euro(total_amount - totaalExcl)}</Text>
              </View>
            )}
            <View style={[s.specRij, { borderTopWidth: 1, borderTopColor: '#ccc', paddingTop: 4, marginTop: 4, marginBottom: 0 }]}>
              <Text style={s.specBoldLabel}>{t('pdf.teBetalen')}</Text>
              <Text style={isFinal ? s.specBoldValueRood : s.specBoldValue}>{euro(total_amount)}</Text>
            </View>
          </View>
        </View>

        {/* ── VOETNOOT ───────────────────────────────────────────── */}
        {invoiceFooter ? (
          <View style={s.footnote}>
            <Text>{invoiceFooter}</Text>
          </View>
        ) : null}

        {/* ── PAGINA-FOOTER (fixed) ───────────────────────────────── */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>
            {companyName}{companyEmail ? ` · ${companyEmail}` : ''}
          </Text>
          <Text style={s.footerText}>{invoice_number}</Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) =>
              `${t('pdf.pagina')} ${pageNumber} ${t('pdf.van2')} ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
